import { type ErrorResponse } from '#/lib/server-util';

import { useRouter } from 'next/navigation';
import { UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';

export { useFetch };

function useFetch() {
  const router = useRouter();

  return {
    get: request('GET'),
    post: request('POST'),
    formPost: request('POST', true),
    put: request('PUT'),
    patch: request('PATCH'),
    delete: request('DELETE'),
    upload: request('POST', true),
    download: request('POST', false, true),
    formDownload: request('POST', true, true),
    postApiKey: requestApiKey('POST')
  };

  function request(method: string, formData?: boolean, isDownLoad?: boolean) {
    return (
      url: string,
      body?: any,
      form?: UseFormReturn<any>,
      timeout: number = 300000
    ) => {
      const requestOptions: any = {
        method,
        timeout
      };
      if (body) {
        if (formData) {
          const data = new FormData();
          Object.keys(body).forEach((k) => {
            if (Array.isArray(body[k])) {
              body[k].forEach((item: any) => {
                if (item instanceof File) {
                  data.append(k, item);
                } else if (typeof item === 'object') {
                  data.append(k, JSON.stringify(item)); // 객체를 JSON 문자열로 변환
                } else {
                  data.append(k, item);
                }
              });
            } else if (body[k] instanceof FileList) {
              // FileList일 경우 처리
              Array.from(body[k] as FileList).forEach((file: File) => {
                data.append(k, file); // FileList 내의 각 파일을 FormData에 추가
              });
            } else if (body[k] instanceof File) {
              data.append(k, body[k]);
            } else {
              if (typeof body[k] === 'object') {
                data.set(k, JSON.stringify(body[k])); // 단일 객체도 JSON 문자열로 변환
              } else {
                data.set(k, body[k]);
              }
            }
          });
          requestOptions.body = data;
        } else {
          requestOptions.headers = { 'Content-Type': 'application/json' };
          requestOptions.body = JSON.stringify(body);
        }
      }
      console.log(body);
      console.log(requestOptions.body);
      return fetch(url, requestOptions).then((res) =>
        handleResponse(res, form, isDownLoad)
      );
    };
  }

  function requestApiKey(method: string) {
    return (
      url: string,
      apiKey: string,
      body?: any,
      timeout: number = 300000
    ) => {
      console.log(url);
      const requestOptions: any = {
        method,
        timeout
      };
      if (body) {
        requestOptions.headers = {
          'Content-Type': 'application/json',
          'api-key': apiKey
        };
        requestOptions.body = JSON.stringify(body);
      }
      return fetch(url, requestOptions).then((res) => handleResponse(res));
    };
  }

  // helper functions

  async function handleResponse(
    response: Response,
    form?: UseFormReturn<any>,
    isDownLoad?: boolean
  ) {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    // JSON 응답 처리
    const data = isJson ? await response.json() : null;

    // 에러 처리 로직
    let checked = false;
    if (!response.ok) {
      if (response.status === 401) {
        router.push('/login');
      } else {
        const body = data as ErrorResponse;
        if (response.status === 409) {
          if (body.errors) {
            body.errors.forEach((name) => {
              form?.setError(name, { message: '이미 추가된 정보입니다.' });
            });
            toast.error('입력 값을 확인하세요.');
          } else {
            toast.error('이미 추가된 정보입니다.');
          }
          checked = true;
        } else if (response.status === 205 && isDownLoad) {
          return true;
        } else if (response.status === 400) {
          console.log(body);
          toast.error(`에러 실패했습니다.`);
          body?.errors?.forEach((e) => {
            toast.error(
              `유효성 검사에 실패했습니다. [${e.path.join(', ')}: ${e.message}] `
            );
          });
          checked = true;
        } else if (response.status === 403) {
          if (data === null) {
            const error = await response.text();
            toast.error(error);
            return Promise.reject({ checked, response, data, error });
          }
          console.log(response);
        } else {
          console.log(response);
        }
      }

      return Promise.reject({ checked, response, data });
    }

    if (
      response.status === 400 &&
      isDownLoad &&
      contentType?.includes('application/octet-stream')
    ) {
      // 파일 다운로드 처리
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      let fileName = 'download.bin';

      // 파일 이름 설정
      const disposition = response.headers.get('Content-Disposition');
      const fileNameMatchStar = disposition?.match(
        /filename\*=UTF-8''([^;\n]+)/
      );
      if (fileNameMatchStar) {
        fileName = decodeURIComponent(fileNameMatchStar[1]);
      } else {
        // fallback: filename="..." 형식 체크
        const fileNameMatch = disposition?.match(/filename="(.+?)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }
      a.download = fileName;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return true;
    }

    return data;
  }
}

// @ts-ignore
BigInt.prototype.toJSON = function (): string {
  return this.toString();
};
